import { styled } from '@mui/material/styles';
import { IconButton, Slider, ListItemButton, Box, Drawer, AppBar, BottomNavigation, Typography } from '@mui/material';

//SiderBar styled comps-------Start
export const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(6)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(6)} + 1px)`,
  },
});

export const StyledSlider = styled(Slider)(({ theme }) => ({
  width: 100,
  color: theme.palette.action.active,
  transition: theme.transitions.create(['color'], {
    duration: theme.transitions.duration.short,
  }),
  '& .MuiSlider-thumb': {
    border: 'none',
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: `0 0 0 8px ${theme.palette.action.active}33`,
    },
  },
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-rail': {
    border: 'none',
    opacity: 0.3,
  },
}));


export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export const PopoverListButtons = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  color: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  width: 60,
  height: 60,
  padding: theme.spacing(1),
  margin: 0,
  transition: theme.transitions.create(['background-color', 'transform', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.active,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.action.focus}`,
    outlineOffset: 2,
  },
}))
export const PopoverFontColorButtons = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  color: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  width: 60,
  height: 60,
  marginBottom: theme.spacing(2.5),
  transition: theme.transitions.create(['background-color', 'transform', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.active,
    transform: 'scale(1.05)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.action.focus}`,
    outlineOffset: 2,
  },
}))
export const StyledListButton = styled(ListItemButton)(({ theme, active }) => ({
  display: 'flex',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: active ? theme.palette.action.selected : 'transparent',
  transition: theme.transitions.create(['background-color', 'color', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    color: theme.palette.action.hover,
    backgroundColor: theme.palette.action.active,
    transform: 'translateX(4px)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.action.focus}`,
    outlineOffset: 2,
  },
}))
export const StyledSubMenuContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  display: 'flex',
  gap: theme.spacing(1.25),
  flexDirection: 'column',
  background: 'linear-gradient(360deg, #0e142b -40%, #506c8a 190%)',
  border: `1px solid ${theme.palette.background.subtle}`,
  borderRadius: theme.shape.borderRadius,
  alignItems: 'center',
  justifyItems: 'center',
  transition: theme.transitions.create(['box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
}));

export const StyledShapeMenuContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
  margin: theme.spacing(1.25),
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

export const StyledSubMenuTitleContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(360deg, #0e142b -100%, #506c8a 140%)',
  border: `1px solid ${theme.palette.background.subtle}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.25, 0),
  width: '100%',
  transition: theme.transitions.create(['box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
}));
export const StyledSubMenuDivisionBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.elevated,
  width: '103.5%',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.25, 0),
}))
export const StyledSubMenu = styled(Typography)(({ theme }) => ({
  textDecoration: 'underline',
  textDecorationColor: theme.palette.text.secondary,
  textDecorationThickness: '2px',
  textEmphasisPosition: 'underleft',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  alignItems: 'center',
  justifyItems: 'center',
  fontWeight: 600,
}))

export const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

export const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  zIndex: theme.zIndex.drawer + 1,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));
//SiderBar styled comps-------End

export const StyledBottomNav = styled(BottomNavigation)(({ theme, open }) => ({
  justifyContent: 'space-between',
  background: theme.palette.background.paper,
  boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.3)',
  marginLeft: open ? 0 : `calc(${theme.spacing(7)} + 1px)`,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));



export const ShapeEditorDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? `${drawerWidth + 160}px` : `calc(${theme.spacing(7)} + 1px)`,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  overflow: 'hidden',
  

  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),

  '& .MuiDrawer-paper': {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Disable scrollbar appearance
    position: 'relative',
    width: '100%',
    scrollbarWidth: 'none', // Hide scrollbar for Firefox
   

    '&::-webkit-scrollbar': {
      display: 'none', // Hide scrollbar for Webkit browsers

      
    },
  },
}));


